import * as React from 'react';

import { subscriptionsWsClient } from '../providers/ApolloProvider';

/**
 * Run `onReconnect` after each subscriptions-socket REconnect, never on the
 * first connect of the socket's life.
 *
 * Every ws disconnect is a loss window — subscription delivery has no replay —
 * so each consumer reconciles once per reconnect. `wasRetry` comes from the
 * client and describes the socket, not this listener's view of it: a local
 * counter would call the first event it happens to see the initial connection,
 * which is wrong for a hook that mounts onto a socket another subscription
 * already opened.
 *
 * The callback is deferred a tick: `connected` fires at the ack, before the
 * client re-sends its subscribe frames, so acting immediately can snapshot a
 * stream that is not registered yet and miss an update landing in between.
 * That narrows the window rather than closing it — each consumer's poll floor
 * is what actually guarantees reconciliation.
 *
 * @param enabled - Subscribe to the socket's lifecycle only while true; the
 *   listener is removed, and any deferred callback cancelled, when it flips off
 *   or the caller unmounts.
 * @param onReconnect - Consumer-specific reconciliation. Read through a ref,
 *   so an unstable identity never churns the socket listener.
 * @returns nothing; the hook exists for its listener side effect.
 */
export function useWsReconnectEffect(enabled: boolean, onReconnect: () => void): void {
  const onReconnectRef = React.useRef(onReconnect);
  React.useEffect(() => {
    onReconnectRef.current = onReconnect;
  });

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    // Tracked so cleanup can cancel it: a reconnect landing in the same tick as
    // an unmount or a gate flip would otherwise still run, and a consumer that
    // invalidates caches would do so on behalf of a hook that is gone.
    const deferred = new Set<ReturnType<typeof setTimeout>>();
    const unlisten = subscriptionsWsClient.on('connected', (_socket, _payload, wasRetry) => {
      if (!wasRetry) {
        return;
      }
      const timer = setTimeout(() => {
        deferred.delete(timer);
        onReconnectRef.current();
      }, 0);
      deferred.add(timer);
    });
    return () => {
      unlisten();
      for (const timer of deferred) {
        clearTimeout(timer);
      }
      deferred.clear();
    };
  }, [enabled]);
}
